import { queryClient } from "App";
import { get } from "lodash";
import { useMemo } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  getEmailUrlsTableColumns,
  getEmailUrlsTableFilters,
} from "routes/email_urls/components/email_urls_table/EmailUrlsTableColumnsAndFilters";
import { EMAIL_URLS_TABLE_CONSTANTS } from "routes/email_urls/components/email_urls_table/EmailUrlsTableV3";
import EmailUrlService from "services/EmailUrls";
import TagService from "services/Tag";
import UserService from "services/User";
import { addResizedColumns } from "./useTableConfig";
import tagConstants from "constants/Tag";

export const invalidateEmailUrlsTableQuery = async () => {
  await queryClient.invalidateQueries({
    queryKey: [EMAIL_URLS_TABLE_CONSTANTS.queryKey],
  });
};

const emailUrlQueryKey = id => ["email-url", String(id)];

export const useEmailUrl = id =>
  useQuery({
    queryKey: emailUrlQueryKey(id),
    queryFn: () => EmailUrlService.getEmailUrl(id),
    enabled: !!id,
    retry: false,
  });

export const useEmailUrlMutations = () => {
  const create = useMutation({
    mutationFn: data => EmailUrlService.createEmailUrl(data),
    onSuccess: () => invalidateEmailUrlsTableQuery(),
  });

  const update = useMutation({
    mutationFn: ({ id, data }) => EmailUrlService.editEmailUrl(id, data),
    onSuccess: (_, { id }) =>
      Promise.all([
        invalidateEmailUrlsTableQuery(),
        queryClient.invalidateQueries({ queryKey: emailUrlQueryKey(id) }),
      ]),
  });

  const remove = useMutation({
    mutationFn: id => EmailUrlService.deleteEmailUrl(id),
    onSuccess: () => invalidateEmailUrlsTableQuery(),
  });

  return { create, update, remove };
};

const fetchTags = async className => {
  const taggableModels = tagConstants.taggableModels;
  const model = Object.keys(taggableModels).find(
    key => taggableModels[key] === className
  );

  const tags = await TagService.getTags({ model: model, for_filtering: true });
  return tags;
};

export const useEmailUrlsTable = ({ className = null, tableName = null }) => {
  const { data: tags, isSuccess: isTagsFetched } = useQuery({
    queryKey: ["tags", className],
    queryFn: () => fetchTags(className),
  });

  const { data: tableSettings, isSuccess: isTableSettingsFetched } = useQuery({
    queryKey: ["table-settings"],
    queryFn: UserService.getUserSettings,
  });

  const filters = useMemo(() => {
    if (!isTagsFetched) {
      return [];
    }

    return getEmailUrlsTableFilters({
      tags,
    });
  }, [tags, isTagsFetched]);

  const columns = useMemo(() => {
    if (!isTableSettingsFetched) return [];
    const initialColumns = getEmailUrlsTableColumns();

    const emailUrlsTableSettings = get(
      tableSettings,
      `user_settings.preferences.tables.${tableName}`,
      {}
    );
    const withResizedColumns = addResizedColumns({
      columns: initialColumns,
      resizedColumns: emailUrlsTableSettings?.resized_columns || {},
    });

    return withResizedColumns;
  }, [tableName, tableSettings, isTableSettingsFetched]);

  return { filters, columns, tags };
};
